import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useEmailAccounts, EmailAccount } from '../../email-accounts/useData';
import { useTheme } from '../../../contexts/ThemeContext';
import { useAuth } from '../../../contexts/AuthContext';
import { useSystemHealthSummary } from '../../system-health/useSystemHealthSummary';
import type { HealthLevel, SystemHealthSummary } from '../../system-health/types';
import HealthBadge from '../../system-health/components/HealthBadge';
import { systemHealthLinkPath } from '../../system-health/utils';
import { formatDateTime, formatDateTimeForList } from '../../../config/appConfig';
import { skeletonPulseClass, viewAllLinkClass, widgetCardClass, widgetTitleClass } from '../themeHelpers';
import { cn } from '../../../lib/utils';

export interface EmailSyncStatusProps {
  limit?: number;
  embedded?: boolean;
  settings?: Record<string, unknown>;
  isEditing?: boolean;
}

type HealthAccount = SystemHealthSummary['emailSync']['accounts'][number];

const levelCardStyles: Record<HealthLevel, string> = {
  ok: 'border-emerald-200/80 bg-emerald-50/40 dark:border-emerald-900/50 dark:bg-emerald-950/20',
  warning: 'border-amber-200/80 bg-amber-50/40 dark:border-amber-900/50 dark:bg-amber-950/20',
  error: 'border-rose-200/80 bg-rose-50/40 dark:border-rose-900/50 dark:bg-rose-950/20',
  info: 'border-sky-200/80 bg-sky-50/40 dark:border-sky-900/50 dark:bg-sky-950/20',
  paused: 'border-slate-200/80 bg-slate-50/50 dark:border-slate-700/50 dark:bg-slate-900/30',
};

function resolveHealthLevel(
  acc: EmailAccount,
  healthAcc: HealthAccount | undefined,
  hasHealthAccess: boolean,
): HealthLevel {
  if (hasHealthAccess && healthAcc) return healthAcc.health;
  if (!acc.syncEnabled) return 'paused';
  if (acc.syncError || acc.syncStatus === 'error') return 'error';
  if (acc.syncStatus === 'syncing') return 'info';
  return 'ok';
}

interface AccountSyncCardProps {
  acc: EmailAccount;
  healthAcc?: HealthAccount;
  hasHealthAccess: boolean;
  colors: ReturnType<typeof useTheme>['colors'];
  onOpen: () => void;
}

const AccountSyncCard: React.FC<AccountSyncCardProps> = ({
  acc,
  healthAcc,
  hasHealthAccess,
  colors,
  onOpen,
}) => {
  const level = resolveHealthLevel(acc, healthAcc, hasHealthAccess);
  const lastSyncAt = healthAcc?.lastSyncAt ?? acc.lastSyncAt;
  const lastSyncLabel = lastSyncAt ? formatDateTimeForList(lastSyncAt) : 'Never';
  const lastSyncTitle = lastSyncAt ? formatDateTime(lastSyncAt) : undefined;
  const emailCount = healthAcc?.emailCount ?? acc._count?.emails;
  const intervalLabel = (healthAcc?.syncEnabled ?? acc.syncEnabled)
    ? `Every ${healthAcc?.syncInterval ?? acc.syncInterval} min`
    : 'Sync disabled';

  const nextSyncLabel =
    hasHealthAccess && healthAcc?.syncEnabled && healthAcc.nextSyncInMinutes != null
      ? healthAcc.nextSyncInMinutes <= 0
        ? 'Due now'
        : `Next in ${healthAcc.nextSyncInMinutes}m`
      : acc.syncEnabled && acc.syncStatus === 'syncing'
        ? 'Syncing now'
        : null;

  const detailMessage =
    (hasHealthAccess && healthAcc?.syncError) ||
    acc.syncError ||
    (hasHealthAccess && healthAcc?.healthMessage) ||
    null;

  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        'w-full text-left rounded-xl border px-3.5 py-3 transition-shadow hover:shadow-sm',
        levelCardStyles[level],
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className={cn('text-sm font-semibold truncate', colors.text)}>
            {acc.displayName || acc.emailAddress}
          </p>
          {acc.displayName && (
            <p className={cn('text-xs truncate mt-0.5', colors.textMuted)}>{acc.emailAddress}</p>
          )}
        </div>
        {hasHealthAccess && healthAcc ? (
          <HealthBadge level={healthAcc.health} className="shrink-0" />
        ) : (
          <HealthBadge level={level} className="shrink-0" />
        )}
      </div>

      <div className={cn('mt-2.5 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-xs')}>
        <div>
          <p className={colors.textMuted}>Last sync</p>
          <p className={cn('font-medium tabular-nums mt-0.5', colors.text)} title={lastSyncTitle}>
            {lastSyncLabel}
          </p>
        </div>
        <div>
          <p className={colors.textMuted}>Schedule</p>
          <p className={cn('font-medium mt-0.5', colors.text)}>
            {nextSyncLabel ? `${nextSyncLabel} · ${intervalLabel}` : intervalLabel}
          </p>
        </div>
        {emailCount != null && (
          <div>
            <p className={colors.textMuted}>Cached emails</p>
            <p className={cn('font-medium tabular-nums mt-0.5', colors.text)}>{emailCount.toLocaleString()}</p>
          </div>
        )}
        {healthAcc?.imapMirrorMode && (
          <div>
            <p className={colors.textMuted}>Mode</p>
            <p className={cn('font-medium mt-0.5 truncate', colors.text)}>{healthAcc.imapMirrorMode}</p>
          </div>
        )}
      </div>

      {detailMessage && (
        <p
          className={cn(
            'mt-2 text-xs leading-snug line-clamp-2',
            level === 'error' ? 'text-rose-700 dark:text-rose-300' : colors.textMuted,
          )}
          title={detailMessage}
        >
          {detailMessage}
        </p>
      )}
    </button>
  );
};

const EmailSyncStatus: React.FC<EmailSyncStatusProps> = ({ limit = 5, embedded, settings }) => {
  const effectiveLimit = (settings?.limit as number) ?? limit;
  const { data: accounts, isLoading } = useEmailAccounts();
  const { colors } = useTheme();
  const navigate = useNavigate();
  const { can } = useAuth();
  const hasHealthAccess = can('system_health', 'view');
  const canManageHealth = can('system_health', 'edit');
  const { data: healthSummary, isLoading: healthLoading } = useSystemHealthSummary();

  const healthByAccountId = React.useMemo(() => {
    const map = new Map<number, HealthAccount>();
    if (healthSummary?.emailSync.accounts) {
      for (const acc of healthSummary.emailSync.accounts) {
        map.set(acc.id, acc);
      }
    }
    return map;
  }, [healthSummary]);

  const cardClass = widgetCardClass(colors, embedded);
  const listClass = embedded ? 'flex-1 min-h-0 overflow-y-auto space-y-2' : 'space-y-2 max-h-[28rem] overflow-y-auto';

  const summary = healthSummary?.emailSync;
  const lastSuccessfulSync = healthSummary?.syncStats?.lastSuccessfulSyncAt;

  if (isLoading || (hasHealthAccess && healthLoading && !healthSummary)) {
    return (
      <div className={cardClass}>
        <h2 className={`${widgetTitleClass(colors)} mb-3`}>Email Sync</h2>
        <div className={`animate-pulse h-32 rounded-lg ${skeletonPulseClass(colors)}`} />
      </div>
    );
  }

  const list = Array.isArray(accounts) ? accounts : [];
  const visibleAccounts = list.slice(0, effectiveLimit);

  return (
    <div className={cn(cardClass, embedded && 'flex flex-col min-h-0')}>
      <div className="flex items-center justify-between mb-3 flex-shrink-0 gap-2">
        <h2 className={widgetTitleClass(colors)}>Email Sync</h2>
        <div className="flex items-center gap-3 shrink-0">
          {hasHealthAccess && (
            <Link to={systemHealthLinkPath(canManageHealth)} className={viewAllLinkClass(colors)}>
              Health
            </Link>
          )}
          <button
            type="button"
            onClick={() => navigate('/email-accounts')}
            className={viewAllLinkClass(colors)}
          >
            Manage
          </button>
        </div>
      </div>

      {hasHealthAccess && summary && summary.total > 0 && (
        <div className={cn('mb-3 flex flex-wrap items-center gap-2 text-xs', colors.textMuted)}>
          <span
            className={cn(
              'inline-flex items-center rounded-full px-2.5 py-1 font-semibold',
              summary.error > 0
                ? colors.error
                : summary.warning > 0
                  ? colors.warning
                  : colors.success,
            )}
          >
            {summary.healthy}/{summary.total} healthy
          </span>
          {lastSuccessfulSync && (
            <span className="tabular-nums">
              Last successful sync · {formatDateTime(lastSuccessfulSync)}
            </span>
          )}
        </div>
      )}

      <div className={listClass}>
        {visibleAccounts.length === 0 ? (
          <div className={cn('py-10 text-center rounded-xl border border-dashed', colors.border, colors.headerBg)}>
            <p className={cn('text-sm font-medium', colors.text)}>No email accounts</p>
            <p className={cn('text-xs mt-1', colors.textMuted)}>Add an account to start syncing mail.</p>
          </div>
        ) : (
          visibleAccounts.map((acc) => (
            <AccountSyncCard
              key={acc.id}
              acc={acc}
              healthAcc={healthByAccountId.get(acc.id)}
              hasHealthAccess={hasHealthAccess}
              colors={colors}
              onOpen={() => navigate(`/email-accounts/${acc.id}`)}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default EmailSyncStatus;
